import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { Button } from 'primereact/button';
import { CrmChecksSideBarFilterType } from './crm-checks-side-bar-filter.type';
import {
  CheckStatusType,
  getCheckStatusDisplayText,
} from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

interface CrmChecksSideBarFilterProps {
  onSearch: (filters: CrmChecksSideBarFilterType) => void;
}

export const CrmChecksSideBarFilter = observer(
  ({ onSearch }: CrmChecksSideBarFilterProps) => {
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [includeUnassignedBranches, setIncludeUnassignedBranches] =
      useState<boolean>(true);
    const [paymentCollectionStartDate, setPaymentCollectionStartDate] =
      useState<Date | null>(null);
    const [paymentCollectionEndDate, setPaymentCollectionEndDate] =
      useState<Date | null>(null);
    const [checkRepaymentStartDate, setCheckRepaymentStartDate] =
      useState<Date | null>(null);
    const [checkRepaymentEndDate, setCheckRepaymentEndDate] =
      useState<Date | null>(null);
    const [selectedCheckStatuses, setSelectedCheckStatuses] = useState<
      CheckStatusType[]
    >([]);
    const [docNumber, setDocNumber] = useState<number | null>(null);
    const [checkReferenceId, setCheckReferenceId] = useState<string | null>(
      null,
    );

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranchIds((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleSearch = () => {
      const filters: CrmChecksSideBarFilterType = {
        branchIds: selectedBranchIds,
        includeUnassignedBranches,
        paymentCollectionStartDate,
        paymentCollectionEndDate,
        checkRepaymentStartDate,
        checkRepaymentEndDate,
        checkStatuses: selectedCheckStatuses,
        docNumber,
        checkReferenceId,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranchIds([]);
      setIncludeUnassignedBranches(true);
      setPaymentCollectionStartDate(null);
      setPaymentCollectionEndDate(null);
      setCheckRepaymentStartDate(null);
      setCheckRepaymentEndDate(null);
      setSelectedCheckStatuses([]);
      setDocNumber(null);
      setCheckReferenceId(null);
    };

    const unassignedBranchOptions = [
      { key: 1, label: 'כלול תשלומים ללא שלוחה' },
    ];

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div
            style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333' }}
          >
            סינונים
          </div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>

        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranchIds}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שלוחות ללא שיוך"
            items={unassignedBranchOptions}
            selectedItems={includeUnassignedBranches ? [1] : []}
            onItemToggle={() => setIncludeUnassignedBranches((prev) => !prev)}
            getItemLabel={(it) => it.label}
            getItemKey={(it) => it.key}
            defaultExpanded={true}
            searchPlaceholder="חפש..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס המחאה"
            items={
              Object.values(CheckStatusType).filter(
                (value) => typeof value === 'string',
              ) as CheckStatusType[]
            }
            selectedItems={selectedCheckStatuses}
            onItemToggle={(status: CheckStatusType) => {
              setSelectedCheckStatuses((prev) =>
                prev.includes(status)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status],
              );
            }}
            getItemLabel={(status) => getCheckStatusDisplayText(status)}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <CustomExpander>
            <div>טווח תאריכי קליטת תשלום</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מתאריך</label>
                <Calendar
                  value={paymentCollectionStartDate}
                  onChange={(e) =>
                    setPaymentCollectionStartDate(e.value as Date)
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>עד תאריך</label>
                <Calendar
                  value={paymentCollectionEndDate}
                  onChange={(e) => setPaymentCollectionEndDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>טווח תאריכי פרעון של המחאות</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מתאריך</label>
                <Calendar
                  value={checkRepaymentStartDate}
                  onChange={(e) => setCheckRepaymentStartDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>עד תאריך</label>
                <Calendar
                  value={checkRepaymentEndDate}
                  onChange={(e) => setCheckRepaymentEndDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>סינון לפי מספר</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מספר מסמך</label>
                <InputNumber
                  value={docNumber}
                  onValueChange={(e) => setDocNumber(e.value ?? null)}
                  useGrouping={false}
                  min={1}
                  style={{ width: '100%', direction: 'ltr' }}
                  inputStyle={{
                    width: '100%',
                    direction: 'rtl',
                    textAlign: 'right',
                  }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>אסמכתא המחאה</label>
                <InputText
                  value={checkReferenceId ?? ''}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '');
                    setCheckReferenceId(digitsOnly ? digitsOnly : null);
                  }}
                  keyfilter="int"
                  style={{
                    width: '100%',
                    direction: 'rtl',
                    textAlign: 'right',
                  }}
                />
              </div>
            </div>
          </CustomExpander>
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            onClick={handleSearch}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    );
  },
);
